import type { AxiosError } from 'axios';
import { TIMEOUT_ERROR_CODE } from './ApiService';

export const isApiRetryableError = (error: unknown): boolean => {
    const err = error as AxiosError;
    return err.code === TIMEOUT_ERROR_CODE || err.response?.status === 503;
};

export type ApiRetryOptions = {
    retries?: number;
    backoffMs?: number;
    backoffMultiplier?: number;
    maxBackoffMs?: number;
    backdrop?: boolean;
};

export async function withApiRetry<T>(
    fn: () => Promise<T>,
    options: ApiRetryOptions = {},
): Promise<T> {
    const {
        retries = 3,
        backoffMs = 1000,
        backoffMultiplier = 2,
        maxBackoffMs = 5000,
        backdrop,
    } = options;

    if (backdrop) window.showBackdrop();
    try {
        let delayMs = backoffMs;

        for (let attempt = 0; attempt <= retries; attempt += 1) {
            try {
                return await fn();
            } catch (error) {
                const isLastAttempt = attempt >= retries;
                if (!isApiRetryableError(error) || isLastAttempt) {
                    throw error;
                }

                const err = error as AxiosError;
                const reason = err.code === TIMEOUT_ERROR_CODE
                    ? 'timeout'
                    : `status=${err.response?.status}`;
                console.warn(`withApiRetry retry=${attempt + 1} reason=${reason}`);

                const retryDelayMs = delayMs;
                await new Promise(resolve => { setTimeout(resolve, retryDelayMs); });
                delayMs = Math.min(delayMs * backoffMultiplier, maxBackoffMs);
            }
        }

        throw new Error('withApiRetry exhausted retries');
    } finally {
        if (backdrop) window.hideBackdrop();
    }
}
